/**
 * Room 3D Viewer Component (Modular)
 *
 * Interactive 3D room visualization using Three.js.
 * Features board-level product rendering, camera-driven transparency,
 * and color-accurate materials.
 */

import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { RotateCcw, Maximize, Minimize, DoorOpen, DoorClosed } from 'lucide-react';

import { Room3DViewerProps, WallFace, GarageConfigProp } from './types';
import { createGarageDoorGroup } from './garageDoorBuilder';
import { createScene, addLighting, addInteriorLight, addLightProbe, ENV_IMAGES, createCamera, configureRenderer, addBorders } from './sceneBuilder';
import {
  createLegacyWallMaterial,
  createLegacyCeilingMaterial,
  createFloorMaterial,
} from './materialFactory';
import {
  INITIAL_ROTATION,
  DEFAULT_FOV,
  applyDragRotation,
  applyZoom,
  interpolateState,
  calculateZoomToFit,
} from './cameraController';
import { attachTouchControls } from './touchController';
import { exitFullscreenSafe, fullscreenSupported, requestFullscreenSafe } from '../../../utils/fullscreen';
import { addDimensionLines, formatDimension } from './dimensionLabels';
import { generateFullRoomLayouts } from './boardLayoutEngine';
import { buildWallMesh, buildCeilingMesh } from './wallMeshBuilder';
import {
  calculateWallOpacities,
  lerpOpacities,
  applyOpacityToGroup,
  TransparencyState,
} from './transparencyController';

import { registerLight, unregisterLight, LightingRegistryEntry } from '../../dev/lightingRegistry';
import '../Room3DViewer.css';

export const Room3DViewer: React.FC<Room3DViewerProps> = ({
  length,
  width,
  height = 10,
  showHeight = true,
  highlightWalls = false,
  highlightCeiling = false,
  highlightFloor = false,
  wallColor = 'White',
  ceilingColor = 'White',
  compact = false,
  showBoards = true,
  wallOrientation = 'Horizontal',
  slatWallConfig = null,
  showCeiling = false,
  garageConfig = null,
}) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const roomGroupRef = useRef<THREE.Group | null>(null);
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const rotationRef = useRef({ ...INITIAL_ROTATION });
  const orbitDistanceRef = useRef(30);
  const roomCenterRef = useRef(new THREE.Vector3(0, 0, 0));
  const animationFrameRef = useRef<number | null>(null);
  const lightingEntryRef = useRef<LightingRegistryEntry | null>(null);
  const floorMeshRef = useRef<THREE.Mesh | null>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);

  // Wall group refs for transparency control
  const wallGroupsRef = useRef<{
    front?: THREE.Group;
    back?: THREE.Group;
    left?: THREE.Group;
    right?: THREE.Group;
    ceiling?: THREE.Group;
  }>({});
  const currentOpacityRef = useRef<TransparencyState>({
    front: 1, back: 1, left: 1, right: 1, ceiling: 1,
  });

  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [hasRotated, setHasRotated] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isInsideRoom, setIsInsideRoom] = React.useState(false);
  const [sceneReady, setSceneReady] = React.useState(false);
  const isInsideRoomRef = useRef(false); // Ref for immediate access during animation
  const viewerWrapperRef = useRef<HTMLDivElement>(null);
  const outsideDistanceRef = useRef(30);

  // Pre-compute board layouts when showBoards is enabled
  const boardLayouts = useMemo(() => {
    if (!showBoards || !showHeight) return null;
    return generateFullRoomLayouts(
      length, width, height,
      wallColor,
      wallOrientation,
      slatWallConfig,
      showCeiling ? ceilingColor : undefined
    );
  }, [length, width, height, wallColor, wallOrientation, slatWallConfig, showCeiling, ceilingColor, showBoards, showHeight]);

  // Render helper — also updates transparency
  const renderScene = () => {
    if (!cameraRef.current || !rendererRef.current || !sceneRef.current) return;

    // Update wall transparency based on camera angle and mode
    if (showBoards && showHeight) {
      const groups = wallGroupsRef.current;

      if (isInsideRoomRef.current) {
        // Inside room: all walls fully opaque
        const allOpaque: TransparencyState = { front: 1, back: 1, left: 1, right: 1, ceiling: 1 };
        currentOpacityRef.current = lerpOpacities(currentOpacityRef.current, allOpaque, 0.2);
      } else {
        // Outside room: camera-driven transparency
        const target = calculateWallOpacities(
          rotationRef.current.y,
          rotationRef.current.x
        );
        currentOpacityRef.current = lerpOpacities(currentOpacityRef.current, target, 0.2);
      }

      if (groups.front) applyOpacityToGroup(groups.front, currentOpacityRef.current.front);
      if (groups.back) applyOpacityToGroup(groups.back, currentOpacityRef.current.back);
      if (groups.left) applyOpacityToGroup(groups.left, currentOpacityRef.current.left);
      if (groups.right) applyOpacityToGroup(groups.right, currentOpacityRef.current.right);
      if (groups.ceiling) applyOpacityToGroup(groups.ceiling, currentOpacityRef.current.ceiling);
    }

    if (isInsideRoomRef.current) {
      // Inside: camera at room center, looks outward based on rotation
      const lookDir = new THREE.Vector3(
        Math.sin(rotationRef.current.y) * Math.cos(rotationRef.current.x),
        Math.sin(rotationRef.current.x),
        Math.cos(rotationRef.current.y) * Math.cos(rotationRef.current.x)
      );
      cameraRef.current.position.copy(roomCenterRef.current);
      const lookTarget = roomCenterRef.current.clone().add(lookDir);
      cameraRef.current.lookAt(lookTarget);
    } else {
      // Outside: orbit around room center
      const pos = new THREE.Vector3(
        orbitDistanceRef.current * Math.sin(rotationRef.current.y) * Math.cos(rotationRef.current.x) + roomCenterRef.current.x,
        orbitDistanceRef.current * Math.sin(rotationRef.current.x) + roomCenterRef.current.y,
        orbitDistanceRef.current * Math.cos(rotationRef.current.y) * Math.cos(rotationRef.current.x) + roomCenterRef.current.z
      );
      cameraRef.current.position.copy(pos);
      cameraRef.current.lookAt(roomCenterRef.current);
    }

    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };

  // Main scene setup
  useEffect(() => {
    if (!containerRef.current) return;

    // Immediately show spinner via DOM before any scene work —
    // this prevents the flash of a half-built scene
    if (spinnerRef.current) {
      spinnerRef.current.style.transition = 'none';
      spinnerRef.current.style.opacity = '1';
      spinnerRef.current.style.pointerEvents = 'auto';
    }
    setSceneReady(false);

    const container = containerRef.current;
    const roomLength = length;
    const roomWidth = width;
    const roomHeight = showHeight ? height : 0.1;

    // Scene
    const scene = createScene();
    sceneRef.current = scene;

    // Lighting
    addLighting(scene);

    // Interior light created after roomGroup (see below)

    // Camera
    const { camera, distance } = createCamera(
      container.clientWidth,
      container.clientHeight,
      roomLength,
      roomWidth,
      roomHeight
    );
    const roomCenterY = showHeight ? height / 2 : 0;
    roomCenterRef.current.set(0, roomCenterY, 0);
    orbitDistanceRef.current = distance;
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    configureRenderer(renderer, showHeight);
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Room group
    const roomGroup = new THREE.Group();
    roomGroupRef.current = roomGroup;
    scene.add(roomGroup);

    // Build room
    buildRoom(roomGroup, roomLength, roomWidth, roomHeight);

    // Interior point light — added to roomGroup so it's in room-local coordinates
    if (showHeight) {
      const interiorLight = addInteriorLight(roomGroup, roomLength, roomWidth, roomHeight);
      let hemiLight: THREE.HemisphereLight | null = null;
      scene.traverse(child => {
        if (child instanceof THREE.HemisphereLight) hemiLight = child;
      });

      const entry: LightingRegistryEntry = {
        viewerType: 'room',
        light: interiorLight,
        hemiLight,
        probe: null,
        envMap: null,
        floorMesh: floorMeshRef.current,
        renderer,
        scene,
        roomGroup,
        renderScene,
        getRotation: () => ({ ...rotationRef.current }),
        roomDimensions: { length: roomLength, width: roomWidth, height: roomHeight },
      };
      lightingEntryRef.current = entry;
      registerLight(entry);

      // Ensure scene becomes visible even if probe loading fails
      const readyTimeout = setTimeout(() => {
        renderScene();
        setSceneReady(true);
      }, 2000);

      // Load light probe from garage env image
      addLightProbe(scene, renderer, ENV_IMAGES[2].path, (setup) => {
        clearTimeout(readyTimeout);

        entry.probe = setup.probe;
        entry.probe.intensity = 0.4;
        entry.envMap = setup.envMap;

        // scene.environment applies envMap lighting/reflections to all materials
        scene.environment = setup.envMap;

        // Set default envMapIntensity on wall materials (live traversal)
        roomGroup.traverse(child => {
          if (child === floorMeshRef.current) return;
          if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
            child.material.envMapIntensity = 0.5;
          }
        });

        // Notify the panel that probe is now available
        registerLight(entry);
        requestAnimationFrame(() => {
          renderScene();
          setSceneReady(true);
        });
      });
    } else {
      // No interior light (ceiling-only mode) — ready immediately
      requestAnimationFrame(() => {
        renderScene();
        setSceneReady(true);
      });
    }

    // Zoom to fit: calculate optimal distance to fill viewport
    const fitDistance = calculateZoomToFit(camera, roomGroup, rotationRef.current, roomCenterRef.current);
    orbitDistanceRef.current = fitDistance;
    outsideDistanceRef.current = fitDistance;

    // Compute initial transparency and render
    if (showBoards && showHeight) {
      const initialOpacity = calculateWallOpacities(rotationRef.current.y, rotationRef.current.x);
      currentOpacityRef.current = initialOpacity;
      const groups = wallGroupsRef.current;
      if (groups.front) applyOpacityToGroup(groups.front, initialOpacity.front);
      if (groups.back) applyOpacityToGroup(groups.back, initialOpacity.back);
      if (groups.left) applyOpacityToGroup(groups.left, initialOpacity.left);
      if (groups.right) applyOpacityToGroup(groups.right, initialOpacity.right);
      if (groups.ceiling) applyOpacityToGroup(groups.ceiling, initialOpacity.ceiling);
    }

    renderScene();

    // Resize handler
    const handleResize = () => {
      if (!container || !cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = container.clientWidth / container.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(container.clientWidth, container.clientHeight);
      renderScene();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (lightingEntryRef.current) {
        unregisterLight(lightingEntryRef.current);
        lightingEntryRef.current = null;
      }
      if (container && rendererRef.current) container.removeChild(rendererRef.current.domElement);
      rendererRef.current?.dispose();
      wallGroupsRef.current = {};
    };
  }, [length, width, height, showHeight, highlightWalls, highlightCeiling, highlightFloor, wallColor, ceilingColor, boardLayouts, showBoards, garageConfig]);

  // Wheel zoom + touch controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const wheelHandler = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (!cameraRef.current) return;
      cameraRef.current.fov = applyZoom(cameraRef.current.fov, e.deltaY);
      cameraRef.current.updateProjectionMatrix();
      renderScene();
    };

    container.addEventListener('wheel', wheelHandler, { passive: false });

    const detachTouch = attachTouchControls({
      container,
      getRotation: () => rotationRef.current,
      setRotation: (r) => { rotationRef.current = r; },
      getFov: () => cameraRef.current?.fov ?? 50,
      setFov: (fov) => {
        if (cameraRef.current) {
          cameraRef.current.fov = fov;
          cameraRef.current.updateProjectionMatrix();
        }
      },
      onUpdate: () => renderScene(),
      onDragStart: () => setHasRotated(true),
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      detachTouch();
    };
  }, []);

  const buildRoom = (
    roomGroup: THREE.Group,
    roomLength: number,
    roomWidth: number,
    roomHeight: number
  ) => {
    // Dispose all old geometry/materials before rebuilding
    roomGroup.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else if (child.material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.LineSegments) {
        child.geometry?.dispose();
        if (child.material instanceof THREE.Material) child.material.dispose();
      }
    });
    while (roomGroup.children.length > 0) roomGroup.remove(roomGroup.children[0]);
    wallGroupsRef.current = {};

    // Floor
    const floorMat = createFloorMaterial(highlightFloor, () => renderScene());
    const floorGeo = new THREE.PlaneGeometry(roomLength, roomWidth);
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(roomLength / 2, 0, roomWidth / 2);
    floor.receiveShadow = true;
    floorMeshRef.current = floor;
    roomGroup.add(floor);

    if (showHeight) {
      if (boardLayouts && showBoards) {
        // Board-level rendering with per-wall groups for transparency
        const wallFaces: WallFace[] = ['front', 'right', 'back', 'left'];
        const faceKeys = ['front', 'right', 'back', 'left'] as const;

        for (let i = 0; i < boardLayouts.walls.length; i++) {
          const wallGroup = buildWallMesh(
            boardLayouts.walls[i],
            wallFaces[i],
            roomLength, roomWidth, roomHeight
          );
          roomGroup.add(wallGroup);
          (wallGroupsRef.current as any)[faceKeys[i]] = wallGroup;
        }

        // Garage doors — overlay on the wall selected by onWallType
        if (garageConfig?.enabled && garageConfig.numDoors > 0) {
          const isLengthWall = garageConfig.onWallType === 'LengthWall';
          const face: WallFace = isLengthWall ? 'front' : 'right';
          const wallLen = isLengthWall ? roomLength : roomWidth;
          const doorGroup = createGarageDoorGroup(
            garageConfig,
            wallLen,
            face,
            roomLength,
            roomWidth
          );
          roomGroup.add(doorGroup);
        }

        // Ceiling — only show if ceiling is included
        if (boardLayouts.ceiling) {
          const ceilingGroup = buildCeilingMesh(boardLayouts.ceiling, roomLength, roomWidth, roomHeight);
          roomGroup.add(ceilingGroup);
          wallGroupsRef.current.ceiling = ceilingGroup;
        }
      } else {
        // Legacy mode
        const wallMaterial = createLegacyWallMaterial(wallColor, highlightWalls);
        const ceilingMat = createLegacyCeilingMaterial(ceilingColor, highlightCeiling);

        const frontGeo = new THREE.PlaneGeometry(roomLength, roomHeight);
        const frontWall = new THREE.Mesh(frontGeo, wallMaterial.clone());
        frontWall.position.set(roomLength / 2, roomHeight / 2, 0);
        addBorders(frontGeo, frontWall);
        roomGroup.add(frontWall);

        const backGeo = new THREE.PlaneGeometry(roomLength, roomHeight);
        const backWall = new THREE.Mesh(backGeo, wallMaterial.clone());
        backWall.position.set(roomLength / 2, roomHeight / 2, roomWidth);
        backWall.rotation.y = Math.PI;
        addBorders(backGeo, backWall);
        roomGroup.add(backWall);

        const leftGeo = new THREE.PlaneGeometry(roomWidth, roomHeight);
        const leftWall = new THREE.Mesh(leftGeo, wallMaterial.clone());
        leftWall.position.set(0, roomHeight / 2, roomWidth / 2);
        leftWall.rotation.y = Math.PI / 2;
        addBorders(leftGeo, leftWall);
        roomGroup.add(leftWall);

        const rightGeo = new THREE.PlaneGeometry(roomWidth, roomHeight);
        const rightWall = new THREE.Mesh(rightGeo, wallMaterial.clone());
        rightWall.position.set(roomLength, roomHeight / 2, roomWidth / 2);
        rightWall.rotation.y = -Math.PI / 2;
        addBorders(rightGeo, rightWall);
        roomGroup.add(rightWall);

        const ceilingGeo = new THREE.PlaneGeometry(roomLength, roomWidth);
        const ceiling = new THREE.Mesh(ceilingGeo, ceilingMat);
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.set(roomLength / 2, roomHeight, roomWidth / 2);
        addBorders(ceilingGeo, ceiling);
        roomGroup.add(ceiling);
      }
    }

    addDimensionLines(roomGroup, roomLength, roomWidth, roomHeight, showHeight, length, width, height);
    roomGroup.position.set(-roomLength / 2, 0, -roomWidth / 2);
  };

  // Enter/Exit Room toggle — smooth animated camera transition
  // Uses direct camera position blending to avoid state timing issues
  const toggleInsideRoom = useCallback(() => {
    if (isAnimating || !cameraRef.current) return;
    const entering = !isInsideRoomRef.current;

    setIsAnimating(true);

    const startRotation = { ...rotationRef.current };
    const startDistance = orbitDistanceRef.current;
    const startFov = cameraRef.current.fov;

    if (entering) {
      outsideDistanceRef.current = orbitDistanceRef.current;
    }

    const targetDistance = entering ? 0 : outsideDistanceRef.current;
    const targetRotation = entering ? { x: 0, y: 0 } : { ...INITIAL_ROTATION };
    const targetFov = entering ? 75 : DEFAULT_FOV;
    // Switch camera mode at the halfway point
    const switchAt = 0.5;

    const duration = 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Interpolate rotation and FOV
      rotationRef.current = {
        x: startRotation.x + (targetRotation.x - startRotation.x) * eased,
        y: startRotation.y + (targetRotation.y - startRotation.y) * eased,
      };
      orbitDistanceRef.current = startDistance + (targetDistance - startDistance) * eased;

      if (cameraRef.current) {
        cameraRef.current.fov = startFov + (targetFov - startFov) * eased;
        cameraRef.current.updateProjectionMatrix();
      }

      // Switch mode at halfway point
      if (entering && eased >= switchAt && !isInsideRoomRef.current) {
        isInsideRoomRef.current = true;
      } else if (!entering && eased >= switchAt && isInsideRoomRef.current) {
        isInsideRoomRef.current = false;
      }

      renderScene();

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isInsideRoomRef.current = entering;
        setIsInsideRoom(entering);
        orbitDistanceRef.current = targetDistance;
        rotationRef.current = { ...targetRotation };
        setIsAnimating(false);
        setHasRotated(false);
        animationFrameRef.current = null;
        renderScene();
      }
    };

    animate();
  }, [isAnimating]);

  // Fullscreen toggle. iPhone Safari has no Element.requestFullscreen — the
  // guarded helpers never call what isn't there, and the button is hidden.
  const canFullscreen = fullscreenSupported();
  const toggleFullscreen = useCallback(() => {
    if (!viewerWrapperRef.current) return;
    if (!document.fullscreenElement) {
      void requestFullscreenSafe(viewerWrapperRef.current).then((ok) => { if (ok) setIsFullscreen(true); });
    } else {
      void exitFullscreenSafe().then((ok) => { if (ok) setIsFullscreen(false); });
    }
  }, []);

  // Listen for fullscreen changes — resize renderer to match new container size
  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      // Delay resize to let the DOM settle after fullscreen transition
      setTimeout(() => {
        const container = containerRef.current;
        if (container && cameraRef.current && rendererRef.current) {
          cameraRef.current.aspect = container.clientWidth / container.clientHeight;
          cameraRef.current.updateProjectionMatrix();
          rendererRef.current.setSize(container.clientWidth, container.clientHeight);
          renderScene();
        }
      }, 100);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const resetView = () => {
    if (isAnimating || !cameraRef.current) return;
    setIsAnimating(true);
    const start = { rotation: { ...rotationRef.current }, fov: cameraRef.current.fov };
    // Inside room: reset to looking straight at back wall. Outside: reset to default orbit.
    const targetRotation = isInsideRoomRef.current ? { x: 0, y: 0 } : { ...INITIAL_ROTATION };
    const targetFov = isInsideRoomRef.current ? 75 : DEFAULT_FOV;
    const target = { rotation: targetRotation, fov: targetFov };
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const state = interpolateState(start, target, progress);
      rotationRef.current = state.rotation;
      if (cameraRef.current) {
        cameraRef.current.fov = state.fov;
        cameraRef.current.updateProjectionMatrix();
      }
      renderScene();
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setHasRotated(false);
        animationFrameRef.current = null;
      }
    };
    animate();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    const deltaX = e.clientX - previousMouseRef.current.x;
    const deltaY = e.clientY - previousMouseRef.current.y;
    rotationRef.current = applyDragRotation(rotationRef.current, deltaX, deltaY);
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
    setHasRotated(true);
    renderScene();
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  return (
    <div ref={viewerWrapperRef} className={`room-3d-viewer ${compact ? 'compact' : ''} ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Dimension card — centered on top border of viewer */}
      <div className="viewer-dimension-card">
        {showHeight
          ? t('viewer.roomDims', {
              length: formatDimension(length),
              width: formatDimension(width),
              height: formatDimension(height),
            })
          : t('viewer.ceilingDims', {
              length: formatDimension(length),
              width: formatDimension(width),
            })
        }
      </div>
      <div
        ref={containerRef}
        className="viewer-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Loading spinner overlay — shown instantly on rebuild, fades out when ready */}
        <div
          ref={spinnerRef}
          style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: '#f9fafb', zIndex: 2, borderRadius: '8px',
            opacity: sceneReady ? 0 : 1,
            pointerEvents: sceneReady ? 'none' : 'auto',
            transition: sceneReady ? 'opacity 0.5s ease-in-out' : 'none',
          }}
        >
          <div style={{
            width: '32px', height: '32px', border: '3px solid #e5e7eb',
            borderTopColor: '#6b7280', borderRadius: '50%',
            animation: 'viewer-spin 0.8s linear infinite',
          }} />
        </div>
      </div>

      <div className="viewer-hint">{t('viewer.hint')}{isFullscreen ? t('viewer.hintEsc') : ''}</div>
      <div className="viewer-controls">
        <button
          className="btn btn-secondary"
          onClick={resetView}
          disabled={!hasRotated || isAnimating}
          title={t('viewer.resetCamera')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
            fontSize: 'var(--font-size-xs)', fontWeight: 600,
            cursor: (hasRotated && !isAnimating) ? 'pointer' : 'not-allowed',
            opacity: (hasRotated && !isAnimating) ? 1 : 0.5, transition: 'all 0.2s ease',
          }}
        >
          <RotateCcw size={14} />
          {t('viewer.resetView')}
        </button>
        {showHeight && showBoards && (
          <button
            className="btn btn-secondary"
            onClick={toggleInsideRoom}
            disabled={isAnimating}
            title={isInsideRoom ? t('viewer.exitRoomTitle') : t('viewer.enterRoomTitle')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600,
              cursor: !isAnimating ? 'pointer' : 'not-allowed',
              opacity: !isAnimating ? 1 : 0.5, transition: 'all 0.2s ease',
            }}
          >
            {isInsideRoom ? <DoorClosed size={14} /> : <DoorOpen size={14} />}
            {isInsideRoom ? t('viewer.exitRoom') : t('viewer.enterRoom')}
          </button>
        )}
        {canFullscreen && (
          <button
            className="btn btn-secondary"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
            {isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Room3DViewer;
